import {
  adaptStoreToFilterPath,
  adaptStoreToPageURL,
} from 'src/adapters/storeToPageURL/adaptStoreToPageURL';
import { deriveTaxonomyLevelsFromFacets } from 'src/adapters/storeToPageURL/deriveTaxonomyLevelsFromFacets';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { TriggerName } from 'src/redux/products/types';

const sortAndPageFromState = (state: ReduxApplicationState, productsQuery = state.productsQuery) =>
  adaptStoreToPageURL({ ...state, productsQuery }, productsQuery.page);

const getActiveFacetValuesByAlias = (
  facets: ReduxApplicationState['productsQuery']['facets'],
): Map<string, Set<string>> => {
  return new Map(
    facets.map((facet) => [
      facet.alias,
      new Set(facet.options.filter((option) => option.active).map((option) => option.value)),
    ]),
  );
};

const getFacetToggleCounts = (
  previousFacets: ReduxApplicationState['productsQuery']['facets'],
  currentFacets: ReduxApplicationState['productsQuery']['facets'],
): { activatedCount: number; deactivatedCount: number } => {
  const previousActive = getActiveFacetValuesByAlias(previousFacets);
  const currentActive = getActiveFacetValuesByAlias(currentFacets);

  let activatedCount = 0;
  let deactivatedCount = 0;

  previousActive.forEach((previousValues, alias) => {
    const currentValues = currentActive.get(alias) ?? new Set<string>();

    previousValues.forEach((value) => {
      if (!currentValues.has(value)) {
        deactivatedCount += 1;
      }
    });

    currentValues.forEach((value) => {
      if (!previousValues.has(value)) {
        activatedCount += 1;
      }
    });
  });

  return { activatedCount, deactivatedCount };
};

const hasActiveFacets = (facets: ReduxApplicationState['productsQuery']['facets']): boolean =>
  facets.some((facet) => facet.options.some((option) => option.active));

// Signature that distinguishes both `/-/...` filters and taxonomy-level selections. Works
// uniformly for taxonomy and non-taxonomy pages — taxonomy levels collapse to `[]` when
// `taxonomyAttributeAliases` is empty, so the suffix is a constant for non-taxonomy state.
const filterSignature = (productsQuery: ReduxApplicationState['productsQuery']): string => {
  const filterPath = adaptStoreToFilterPath({ productsQuery });
  const levels = deriveTaxonomyLevelsFromFacets(
    productsQuery.facets,
    productsQuery.taxonomyAttributeAliases,
  );
  return `${filterPath}|${JSON.stringify(levels)}`;
};

export const getRouteChangeTriggerName = (
  previousState: ReduxApplicationState,
  currentState: ReduxApplicationState,
): TriggerName => {
  const currentPq = previousState.productsQuery;
  const nextPq = currentState.productsQuery;

  const current = sortAndPageFromState(previousState, currentPq);
  const next = sortAndPageFromState(currentState, nextPq);

  const sortChanged = current.get('sort') !== next.get('sort');
  const pageChanged = current.get('page') !== next.get('page');
  const filterChanged = filterSignature(currentPq) !== filterSignature(nextPq);

  const { activatedCount, deactivatedCount } = getFacetToggleCounts(
    currentPq.facets,
    nextPq.facets,
  );
  const nextHasNoActiveFacets = !hasActiveFacets(nextPq.facets);

  if (pageChanged && !filterChanged && !sortChanged) {
    return TriggerName.PAGE_NUMBER_CHANGE;
  }
  if (sortChanged && !filterChanged) {
    return TriggerName.CHANGE_SORT;
  }

  if (filterChanged && deactivatedCount > 0 && activatedCount === 0) {
    if (deactivatedCount === 1 && !sortChanged) {
      return TriggerName.RESET_FACET;
    }

    if (nextHasNoActiveFacets) {
      return TriggerName.RESET_ALL_FACETS_AND_SORT;
    }
  }

  return TriggerName.CHANGE_FACETS;
};
